// Import the supabase client
import { supabase } from '@/integrations/supabase/client';

// Function to generate general screening questions using OpenAI
export const generateGeneralQuestions = async (role: string, level: string): Promise<string[]> => {
  try {
    // Call the Supabase Edge Function to generate screening questions
    const { data, error } = await supabase.functions.invoke('openai-assessment-helper', {
      body: {
        action: 'generateScreeningQuestions',
        data: {
          role,
          level
        }
      }
    });

    if (error) throw error;

    // Return the screening questions from the response
    return data.screeningQuestions || [
      // Fallback to hardcoded questions if API fails
      `What is your expected salary range for this ${level} ${role} position?`,
      `What is your notice period or how soon would you be available to start if selected?`,
      `Do you have any preferences regarding work location or remote work options?`
    ];
  } catch (error) {
    console.error('Error generating screening questions:', error);
    // Return fallback questions on error
    return [
      `What is your expected salary range for this ${level} ${role} position?`,
      `What is your notice period or how soon would you be available to start if selected?`,
      `Do you have any preferences regarding work location or remote work options?`
    ];
  }
};

// Add the new function that uses the generateScreeningQuestions endpoint
export const generateScreeningQuestions = async (role: string, level: string): Promise<string[]> => {
  try {
    // Call the Supabase Edge Function to generate screening questions
    const { data, error } = await supabase.functions.invoke('openai-assessment-helper', {
      body: {
        action: 'generateScreeningQuestions',
        data: {
          role,
          level
        }
      }
    });

    if (error) throw error;

    // Return the screening questions from the response
    return data.screeningQuestions || [
      // Fallback to hardcoded questions if API fails
      `What is your expected salary range for this ${level} ${role} position?`,
      `What is your notice period or how soon would you be available to start if selected?`,
      `Do you have any preferences regarding work location or remote work options?`
    ];
  } catch (error) {
    console.error('Error generating screening questions:', error);
    // Return fallback questions on error
    return [
      `What is your expected salary range for this ${level} ${role} position?`,
      `What is your notice period or how soon would you be available to start if selected?`,
      `Do you have any preferences regarding work location or remote work options?`
    ];
  }
};

// Function to generate skill-specific questions using OpenAI
export const generateSkillQuestions = async (role: string, level: string, skill: string): Promise<string[]> => {
  try {
    // Call the Supabase Edge Function to generate assessment questions
    const { data, error } = await supabase.functions.invoke('openai-assessment-helper', {
      body: {
        action: 'generateAssessmentQuestions',
        data: {
          role,
          level,
          skills: [skill] // Single skill in array
        }
      }
    });

    if (error) throw error;

    // Return the questions for the specified skill
    return data.skillQuestions?.[skill] || [
      // Fallback to hardcoded questions if API fails
      `Can you describe a challenging project where you applied ${skill} in a ${role} context?`,
      `How would you rate your proficiency in ${skill} from 1-10, and why?`,
      `What specific ${skill} tools or frameworks are you most comfortable with?`
    ];
  } catch (error) {
    console.error(`Error generating ${skill} questions:`, error);
    // Return fallback questions on error
    return [
      `Can you describe a challenging project where you applied ${skill} in a ${role} context?`,
      `How would you rate your proficiency in ${skill} from 1-10, and why?`,
      `What specific ${skill} tools or frameworks are you most comfortable with?`
    ];
  }
};

// Function to generate questions for all given skills at once
export const generateAllSkillQuestions = async (
  role: string, 
  level: string, 
  skills: string[]
): Promise<Record<string, string[]>> => {
  try {
    // Call the Supabase Edge Function to generate assessment questions for all skills
    const { data, error } = await supabase.functions.invoke('openai-assessment-helper', {
      body: {
        action: 'generateAssessmentQuestions',
        data: {
          role,
          level,
          skills
        }
      }
    });

    if (error) throw error;

    // Return the questions for all skills
    return data.skillQuestions || {};
  } catch (error) {
    console.error('Error generating all skill questions:', error);
    
    // If API fails, fallback to generating questions for each skill individually
    try {
      const skillQuestionsMap: Record<string, string[]> = {};
      
      // In a production environment, we would batch these requests
      // or create a more efficient prompt to handle all skills at once
      await Promise.all(
        skills.map(async (skill) => {
          skillQuestionsMap[skill] = [
            `Can you describe a challenging project where you applied ${skill} in a ${role} context?`,
            `How would you rate your proficiency in ${skill} from 1-10, and why?`,
            `What specific ${skill} tools or frameworks are you most comfortable with?`
          ];
        })
      );
      
      return skillQuestionsMap;
    } catch (fallbackError) {
      console.error('Fallback error:', fallbackError);
      return {};
    }
  }
};

// Function to evaluate the quality of questions and provide improvements
export const improveQuestion = async (question: string, skill: string | null = null): Promise<string> => {
  try {
    // In a real implementation, this would make an API call to OpenAI
    // For now, we'll just return the original question
    return question;
  } catch (error) {
    console.error('Error improving question:', error);
    return question;
  }
}; 